import { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/track";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleSignIn = () => {
    trackEvent("google_signin_started");
    const redirectUrl = window.location.origin + "/auth/google/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Auth() {
    const { user, login, register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (params.get("mode") === "signup") setMode("signup");
    }, [params]);

    if (user) return <Navigate to="/home" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "login") {
                await login(email, password);
                trackEvent("signin_completed", { provider: "email" });
            } else {
                if (password.length < 6) throw new Error("Password must be at least 6 characters");
                // Name is auto-derived from email on the server.
                await register(email, password);
                trackEvent("signup_completed", { provider: "email" });
            }
            toast.success(mode === "login" ? "Welcome back" : "Welcome to Cadence");
            navigate("/home", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Something went wrong";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen vf-grain flex items-center justify-center p-6" data-testid="auth-page">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md"
            >
                <div className="flex items-center gap-3 justify-center mb-8">
                    <div className="w-10 h-10 rounded-2xl bg-terracotta flex items-center justify-center text-white">
                        <Mic size={20} strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="font-serif text-3xl tracking-tight leading-none">Cadence</div>
                        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-400 mt-1">Speak with rhythm</div>
                    </div>
                </div>

                <div className="rounded-3xl border border-stone-200 bg-white/80 dark:bg-[#242322]/80 dark:border-stone-800 backdrop-blur-md p-7">
                    <h1 className="font-serif text-3xl sm:text-4xl tracking-tight font-light mb-1">
                        {mode === "login" ? "Welcome back." : "Find your voice."}
                    </h1>
                    <p className="text-sm text-stone-500 mb-6">
                        {mode === "login"
                            ? "Sign in to continue practicing."
                            : "Create an account to start your first session."}
                    </p>

                    {/* Google sign-in */}
                    <button
                        type="button"
                        onClick={startGoogleSignIn}
                        data-testid="google-sign-in"
                        className="w-full flex items-center justify-center gap-3 rounded-full h-12 border border-stone-300 dark:border-stone-700 bg-white dark:bg-[#1f1e1d] hover:border-stone-500 hover:shadow-sm transition-all text-sm font-medium"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                        </svg>
                        Continue with Google
                    </button>

                    <div className="my-5 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase font-bold text-stone-300">
                        <div className="flex-1 h-px bg-stone-200 dark:bg-stone-700" />
                        or
                        <div className="flex-1 h-px bg-stone-200 dark:bg-stone-700" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="text-xs tracking-[0.2em] uppercase font-bold text-stone-400">Email</label>
                            <Input
                                data-testid="auth-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@studio.com"
                                className="mt-1 rounded-2xl"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs tracking-[0.2em] uppercase font-bold text-stone-400">Password</label>
                            <Input
                                data-testid="auth-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••"
                                className="mt-1 rounded-2xl"
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="auth-submit-button"
                            className="w-full rounded-full h-12 bg-terracotta hover:bg-[#D05A40] text-white text-base font-medium tracking-wide"
                        >
                            {loading ? "Just a moment…" : mode === "login" ? "Sign in" : "Create account"}
                        </Button>
                    </form>

                    <button
                        onClick={() => setMode(mode === "login" ? "register" : "login")}
                        className="w-full mt-5 text-sm text-stone-500 hover:text-terracotta transition-colors"
                        data-testid="auth-toggle-mode"
                    >
                        {mode === "login" ? "New here? Create an account →" : "Already have an account? Sign in →"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
